import { AbstractClient, CompositionClient } from "@/app/client";
import * as types from "@/app/types";
import { UUID } from "crypto";

export default class APIEmployees extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  // List all employees
  get = async (): Promise<types.EmployeeResponse[]> => {
    let res = await this._fetch({
      url: `/employees/`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Employees scoped to a group (unlinked + linked-to-member). Server-side
  // boundary for the agency workspace — never returns out-of-scope records.
  getForGroup = async (groupId: UUID): Promise<types.EmployeeResponse[]> => {
    let res = await this._fetch({
      url: `/groups/${groupId}/employees`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Get single employee
  getEmployeeId = async (id: UUID): Promise<types.EmployeeResponse> => {
    let res = await this._fetch({
      url: `/employees/${id}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Create employee
  post = async (
    data: types.EmployeeCreate,
  ): Promise<types.EmployeeResponse> => {
    let res = await this._fetch({
      url: `/employees/`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
      body: data,
      bodyMime: "application/json",
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Update employee
  putEmployeeId = async (
    id: UUID,
    data: types.EmployeeUpdate,
  ): Promise<types.EmployeeResponse> => {
    let res = await this._fetch({
      url: `/employees/${id}`,
      method: "PUT",
      auth: ["OAuth2PasswordBearer"],
      body: data,
      bodyMime: "application/json",
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Delete employee
  deleteEmployeeId = async (id: UUID): Promise<void> => {
    let res = await this._fetch({
      url: `/employees/${id}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.status === 204) return;
    throw new Error("Bad status code");
  };

  // Link employee to user
  postEmployeeIdLink = async (
    id: UUID,
    data: types.EmployeeLinkRequest,
  ): Promise<types.EmployeeResponse> => {
    let res = await this._fetch({
      url: `/employees/${id}/link`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
      body: data,
      bodyMime: "application/json",
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Unlink employee from user
  postEmployeeIdUnlink = async (id: UUID): Promise<types.EmployeeResponse> => {
    let res = await this._fetch({
      url: `/employees/${id}/unlink`,
      method: "POST",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };

  // Update current user's linked employee
  putMe = async (
    data: types.EmployeeUpdate,
  ): Promise<types.EmployeeResponse> => {
    let res = await this._fetch({
      url: `/employees/me`,
      method: "PUT",
      auth: ["OAuth2PasswordBearer"],
      body: data,
      bodyMime: "application/json",
    });
    if (res.headers.get("Content-Type") === "application/json")
      return res.json();
    throw new Error("Bad content type");
  };
}
